import { useUpdateUserStatusMutation, UserStatusEnum } from '@/@data'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useNotification, useTranslator } from '@/hooks'
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  TextField,
  Typography,
  Box,
  Avatar
} from '@mui/material'

import { Button } from '@/@core/components/mui/Button'

import { useForm, FormProvider, Controller } from 'react-hook-form'

interface IGuestWaitingNoteModal {
  open: boolean
  isPending?: boolean
  setOpen: (open: boolean) => void
  dataRequest: any
  onSuccess: () => void
}

export function GuestWaitingNoteDialog({ open, setOpen, dataRequest, onSuccess }: IGuestWaitingNoteModal) {
  const { __ } = useTranslator()
  const n = useNotification()

  const handleClose = () => {
    setOpen(false)
  }

  const methods = useForm({
    defaultValues: {
      note: ''
    }
  })

  const { mutate: guestWaitingStatus, isPending } = useUpdateUserStatusMutation()

  const handleGuestWaiting = async (data: any) => {
    if (dataRequest?.id) {
      guestWaitingStatus(
        {
          id: [dataRequest.id],
          status: UserStatusEnum.GUEST_WAITING,
          note: data.note ? [{ message: data.note }] : undefined
        },
        {
          onSuccess: () => {
            n.success(__('User status updated to guest waiting successfully'))
            onSuccess()
            handleClose()
          },
          onError: () => {
            n.error(__('Failed to update user status'))
          }
        }
      )
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '1000px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleGuestWaiting)}>
          <DialogCloseButton onClick={handleClose} sx={{ position: 'absolute', right: 0, top: 0, zIndex: 1302 }}>
            <i className='tabler-x' />
          </DialogCloseButton>

          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
              <Avatar
                src={dataRequest?.avatar_file?.path_url || dataRequest?.avatar}
                alt={dataRequest?.first_name}
                sx={{ width: 80, height: 80 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Typography variant='h5' gutterBottom sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}>
                    {dataRequest?.first_name}
                  </Typography>

                  <Button
                    variant='contained'
                    className='bg-[#6F83F6]'
                    endIcon={<i className='tabler-link' />}
                    sx={{ borderRadius: 10, height: '30px', fontSize: '13px' }}
                  >
                    {dataRequest.status}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='text-[#2F2B3D66]'>
                  <i className='tabler-id' />
                  <span className='text-[15px] leading-[22px]'>{dataRequest?.system_code}</span>
                  <i
                    className='tabler-copy'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigator.clipboard.writeText(dataRequest?.system_code || '')
                      n.success(__('Copied to clipboard!'))
                    }}
                  />
                </Box>
              </Box>
            </Box>
            <Box sx={{ mb: 4, border: '1px solid #E0E0E0', borderRadius: 2, p: 3 }}>
              <div>
                <Typography variant='h6' component='span'>
                  {__('Note')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
              </div>
              <div className='mt-3'>
                <Controller
                  name='note'
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={__('Note')}
                      multiline
                      minRows={7}
                      fullWidth
                      sx={{
                        background: '#fff',
                        borderRadius: '10px',
                        '& textarea': {
                          fontSize: '17px',
                          color: '#A8AAAE'
                        }
                      }}
                    />
                  )}
                />
              </div>
            </Box>
          </DialogContent>

          <DialogActions className='px-[20px] sm:px-[24px]'>
            <Button
              variant='contained'
              startIcon={<i className='tabler-arrow-left' />}
              onClick={handleClose}
              sx={{ borderRadius: 10, backgroundColor: '#6F83F629', height: 38, color: '#6F83F6' }}
            >
              {__('Previous')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={isPending}
              endIcon={<i className='tabler-check' />}
              sx={{ borderRadius: 10, height: 38, backgroundColor: '#FF9900', color: '#fff' }}
            >
              {isPending ? (
                <>
                  <CircularProgress size={16} color='inherit' />
                  {__('Updating...')}
                </>
              ) : (
                __('Submit')
              )}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
